/**
 * 指挥页 WebSocket 消息处理（从 Dashboard 拆出）
 */
import { missionIdMatchesMember, isTokenLikeId, deviceNameKey, dedupeMembersForCommanderList } from './deviceIdentity';

/** 空串不把队员打回未知组织/旧节点；非空 org/group 以服务器为准覆盖。 */
function withAuthoritativeOrgGroup(prev, patch) {
  const org = String(patch?.org_name || '').trim();
  const group = String(patch?.group_name || '').trim();
  return {
    ...prev,
    ...patch,
    org_name: org || prev?.org_name || '',
    group_name: group || prev?.group_name || ''
  };
}

export function mergeMemberLocationUpdate(prevMembers = [], payload) {
  if (!payload) return prevMembers;
  const now = Date.now();
  let lastSeen = payload.last_seen;
  const parsed = lastSeen ? new Date(lastSeen).getTime() : NaN;
  // 实时推送到达即视为设备在线；若 GPS/设备时钟偏移，改用本机到达时间，避免「刚上报却显示离线」
  if (!Number.isFinite(parsed) || now - parsed > 60 * 1000 || parsed - now > 5 * 60 * 1000) {
    lastSeen = new Date(now).toISOString();
  }
  const patch = { ...payload, last_seen: lastSeen };
  const patchId = String(patch.device_unique_id || '').trim();
  const patchName = String(patch.name || '').trim();

  // Prefer merging into live uid row when payload is legacy alias (zhxt_app / name-as-id)
  if (patchId && !/^uid:/i.test(patchId)) {
    const uidSibling = (prevMembers || []).find((m) => {
      const mid = String(m?.device_unique_id || '').trim();
      if (!/^uid:/i.test(mid)) return false;
      if (patchName && String(m?.name || '').trim() === patchName) return true;
      if (missionIdMatchesMember(patchId, m) || missionIdMatchesMember(patchName, m)) return true;
      const a = deviceNameKey(m?.name || m?.device_unique_id);
      const b = deviceNameKey(patchName || patchId);
      return a && b && a === b;
    });
    if (uidSibling) {
      const keepId = uidSibling.device_unique_id;
      const merged = (prevMembers || [])
        .filter((m) => String(m?.device_unique_id || '') !== patchId)
        .map((m) => {
          if (m.device_unique_id !== keepId) return m;
          return withAuthoritativeOrgGroup(m, {
            ...patch,
            device_unique_id: keepId,
            name: patchName || m.name
          });
        });
      return dedupeMembersForCommanderList(merged);
    }
  }

  const updated = [...prevMembers];
  // 先按稳定 id 命中；禁止仅凭同名把 uid 行覆盖成别名 id
  let idx = patchId
    ? updated.findIndex((m) => String(m?.device_unique_id || '').trim() === patchId)
    : -1;
  if (idx === -1 && patchName) {
    idx = updated.findIndex((m) => String(m?.name || '').trim() === patchName);
  }
  if (idx === -1) {
    idx = updated.findIndex((m) => missionIdMatchesMember(patchId || patchName, m));
  }
  if (idx !== -1) {
    const prevId = String(updated[idx]?.device_unique_id || '').trim();
    const keepUid = /^uid:/i.test(prevId) && patchId && !/^uid:/i.test(patchId);
    updated[idx] = withAuthoritativeOrgGroup(updated[idx], {
      ...patch,
      device_unique_id: keepUid ? prevId : (patchId || prevId),
      name: patchName || updated[idx].name
    });
  }
  // 名册只来自 HTTP /device/list。WS 只更新已在名单里的人，避免空名单被在线点补成「只剩在线」。
  return dedupeMembersForCommanderList(updated);
}

export function mergeMemberIdentityUpdate(prevMembers = [], payload) {
  if (!payload?.device_unique_id) return prevMembers;
  const patch = {
    name: payload.name,
    group_name: payload.group_name,
    org_name: payload.org_name,
    device_unique_id: payload.device_unique_id
  };
  let found = false;
  let next = (prevMembers || []).map((m) => {
    if (m.device_unique_id !== payload.device_unique_id) return m;
    found = true;
    return withAuthoritativeOrgGroup(m, patch);
  });
  if (!found) next = [...next, patch];

  // drop ghost rows with same display name but different id (legacy zhxt_app / name-as-id)
  if (patch.name && patch.device_unique_id) {
    next = next.filter((m) => {
      if (m.device_unique_id === patch.device_unique_id) return true;
      if (String(m.name || '') !== String(patch.name)) return true;
      if (/^uid:/i.test(String(patch.device_unique_id)) && !/^uid:/i.test(String(m.device_unique_id || ''))) {
        return false;
      }
      if (isTokenLikeId(m.device_unique_id) && !isTokenLikeId(patch.device_unique_id)) {
        return false;
      }
      return true;
    });
  }
  return next;
}

export function upsertById(list = [], item, getId = (x) => x?.id) {
  if (!item) return list;
  const id = String(getId(item));
  if (list.some((x) => String(getId(x)) === id)) {
    return list.map((x) => (String(getId(x)) === id ? item : x));
  }
  return [...list, item];
}

export function appendIfAbsentById(list = [], item, getId = (x) => x?.id) {
  if (!item) return list;
  const id = String(getId(item));
  if (list.some((x) => String(getId(x)) === id)) return list;
  return [...list, item];
}

/**
 * 解析并分发指挥页 WS 消息
 * @param {string} rawData
 * @param {{
 *   onLocationUpdate?: (payload) => void,
 *   onPickPointSubmitted?: (point) => void,
 *   onPickPointProgress?: (point) => void,
 *   onPickPointAmended?: (point) => void,
 *   onPickPointDeleted?: (point) => void,
 *   onRescueTarget?: (target) => void,
 *   onRescueTargetStatus?: (target) => void,
 *   onRescueTrack?: (track) => void,
 *   onRescueTrackStatus?: (track) => void,
 *   onTrackerFieldMessage?: (payload) => void,
 *   onTrackerMessagePinRemoved?: (payload) => void,
 *   onTrackerIdentityUpdate?: (payload) => void,
 *   onTrackerCommandDispatch?: (payload) => void,
 *   onTrackerCommandPinRemoved?: (payload) => void,
 *   onRescueTargetDeleted?: (payload) => void
 * }} handlers
 */
export function handleDashboardWsMessage(rawData, handlers = {}) {
  const message = JSON.parse(rawData);
  const payload = message.payload || message.data;

  switch (message.type) {
    case 'location_update':
      console.log('收到实时位置更新:', message.payload);
      handlers.onLocationUpdate?.(message.payload);
      break;
    case 'tracker_field_message':
      if (payload) {
        console.log('收到前线指令回复:', payload);
        handlers.onTrackerFieldMessage?.(payload);
      }
      break;
    case 'tracker_message_pin_removed':
      if (payload) handlers.onTrackerMessagePinRemoved?.(payload);
      break;
    case 'tracker_identity_update':
      if (payload) {
        console.log('收到队员身份更新:', payload);
        handlers.onTrackerIdentityUpdate?.(payload);
      }
      break;
    case 'tracker_command_dispatch':
      if (payload) {
        console.log('收到外发指令位置推送:', payload);
        handlers.onTrackerCommandDispatch?.(payload);
      }
      break;
    case 'tracker_command_pin_removed':
      if (payload) handlers.onTrackerCommandPinRemoved?.(payload);
      break;
    case 'command_mission_board_updated':
      if (payload) handlers.onCommandMissionBoardUpdated?.(payload);
      break;
    case 'rescue_target_deleted':
      if (payload) handlers.onRescueTargetDeleted?.(payload);
      break;
    case 'pick_point_submitted':
      if (payload) handlers.onPickPointSubmitted?.(payload);
      break;
    case 'pick_point_progress':
      if (payload) handlers.onPickPointProgress?.(payload);
      break;
    case 'pick_point_amended':
      if (payload) handlers.onPickPointAmended?.(payload);
      break;
    case 'pick_point_deleted':
      if (payload) handlers.onPickPointDeleted?.(payload);
      break;
    case 'rescue_target':
      if (payload && payload.status !== '已完结') {
        console.log('收到救援目标推送:', payload);
        handlers.onRescueTarget?.(payload);
      }
      break;
    case 'rescue_target_status':
      if (payload) {
        console.log('收到救援目标完结状态更新:', payload);
        handlers.onRescueTargetStatus?.(payload);
      }
      break;
    case 'track_upload':
    case 'rescue_track':
      if (payload && payload.status !== '已完结') {
        console.log('收到轨迹推送:', payload);
        handlers.onRescueTrack?.(payload);
      }
      break;
    case 'rescue_track_status':
      if (payload) {
        console.log('收到救援轨迹完结状态更新:', payload);
        handlers.onRescueTrackStatus?.(payload);
      }
      break;
    default:
      break;
  }
  return message;
}

/**
 * 组装指挥页 WS 业务 handlers（由 Dashboard 注入 setState / 地图操作）
 */
export function buildDashboardRealtimeHandlers(deps = {}) {
  const {
    membersRef,
    setMembers,
    displayPickPointOnMap,
    removePickPointFromMap,
    setPickPoints,
    displayRescueTargetOnMap,
    removeRescueTargetFromMap,
    setRescueTargets,
    displayRescueTrackOnMap,
    removeRescueTrackFromMap,
    setRescueTracks,
    onTrackerFieldMessage,
    onTrackerMessagePinRemoved,
    onTrackerIdentityUpdate,
    onTrackerCommandDispatch,
    onTrackerCommandPinRemoved,
    onCommandMissionBoardUpdated,
    onRescueTargetDeleted
  } = deps;

  return {
    onLocationUpdate: (payload) => {
      setMembers?.((prev) => {
        const next = mergeMemberLocationUpdate(prev, payload);
        if (membersRef) membersRef.current = next;
        return next;
      });
    },
    onTrackerIdentityUpdate: (payload) => {
      setMembers?.((prev) => {
        const next = mergeMemberIdentityUpdate(prev, payload);
        if (membersRef) membersRef.current = next;
        return next;
      });
      onTrackerIdentityUpdate?.(payload);
    },
    onPickPointSubmitted: (point) => {
      displayPickPointOnMap?.(point);
      setPickPoints?.((previous) => upsertById(previous, point));
    },
    onPickPointProgress: (point) => {
      displayPickPointOnMap?.(point);
      setPickPoints?.((previous) => upsertById(previous, point));
    },
    onPickPointAmended: (point) => {
      displayPickPointOnMap?.(point);
      setPickPoints?.((previous) => upsertById(previous, point));
    },
    onPickPointDeleted: (point) => {
      removePickPointFromMap?.(point.id);
      setPickPoints?.((previous) => previous.filter((item) => String(item.id) !== String(point.id)));
    },
    onRescueTarget: (target) => {
      displayRescueTargetOnMap?.(target);
      setRescueTargets?.((prev) => appendIfAbsentById(prev, target));
    },
    onRescueTargetStatus: (target) => {
      if (target.status === '已完结') {
        removeRescueTargetFromMap?.(target.id);
        return;
      }
      displayRescueTargetOnMap?.(target);
      setRescueTargets?.((prev) => upsertById(prev, target));
    },
    onRescueTrack: (track) => {
      displayRescueTrackOnMap?.(track);
      setRescueTracks?.((prev) => appendIfAbsentById(prev, track));
    },
    onRescueTrackStatus: (track) => {
      if (track.status === '已完结') {
        removeRescueTrackFromMap?.(track.id);
        return;
      }
      displayRescueTrackOnMap?.(track);
      setRescueTracks?.((prev) => upsertById(prev, track));
    },
    onTrackerFieldMessage: (payload) => {
      onTrackerFieldMessage?.(payload);
    },
    onTrackerMessagePinRemoved: (payload) => {
      onTrackerMessagePinRemoved?.(payload);
    },
    onTrackerCommandDispatch: (payload) => {
      onTrackerCommandDispatch?.(payload);
    },
    onTrackerCommandPinRemoved: (payload) => {
      onTrackerCommandPinRemoved?.(payload);
    },
    onCommandMissionBoardUpdated: (payload) => {
      onCommandMissionBoardUpdated?.(payload);
    },
    onRescueTargetDeleted: (payload) => {
      const id = payload?.id;
      if (id == null) return;
      removeRescueTargetFromMap?.(id);
      setRescueTargets?.((prev) => (prev || []).filter((item) => String(item.id) !== String(id)));
      onRescueTargetDeleted?.(payload);
    }
  };
}

/** 构建「队员修改了设备信息」顶部提示 */
export function buildIdentityUpdateNotification(payload) {
  if (!payload) return null;
  const name = payload.name || payload.previous?.name || '未知队员';
  const orgName = payload.org_name || '未知组织';
  const groupName = payload.group_name || '未知小组';
  const prevGroup = payload.previous?.group_name || '';
  const prevOrg = payload.previous?.org_name || '';
  const prevName = payload.previous?.name || '';
  const parts = [];
  if (payload.name_changed && prevName && prevName !== name) {
    parts.push(`姓名 ${prevName} → ${name}`);
  }
  if (payload.group_changed) {
    const from = [prevOrg, prevGroup].filter(Boolean).join('/') || '原小组';
    const to = [orgName, groupName].filter(Boolean).join('/');
    parts.push(`已移至 ${to}（原：${from}）`);
  }
  if (!parts.length) parts.push('更新了设备信息');
  return {
    id: `identity-${Date.now()}-${Math.random()}`,
    type: 'identity_update',
    memberId: payload.device_unique_id || name,
    memberName: name,
    groupName,
    orgName,
    detail: parts.join(' · '),
    timestamp: new Date().toLocaleTimeString()
  };
}

/**
 * 建立指挥页实时 WebSocket
 * @returns {{ ws: WebSocket, close: () => void }}
 */
export function createDashboardRealtimeSocket(options = {}) {
  const {
    wsUrl,
    handlers = {},
    WebSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : null,
    reconnect = true,
    reconnectMinMs = 1000,
    reconnectMaxMs = 15000
  } = options;

  if (!WebSocketImpl || !wsUrl) {
    return {
      ws: null,
      close: () => {}
    };
  }

  let closedByUser = false;
  let ws = null;
  let reconnectTimer = null;
  let attempt = 0;

  const clearReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const bind = (socket) => {
    socket.onopen = () => {
      attempt = 0;
      console.log('WebSocket连接已建立，开始接收实时位置更新');
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
        if (token) {
          socket.send(JSON.stringify({ type: 'auth', token }));
        }
      } catch (_e) {
        /* ignore */
      }
    };

    socket.onmessage = (event) => {
      try {
        handleDashboardWsMessage(event.data, handlers);
      } catch (error) {
        console.error('WebSocket消息解析失败:', error);
      }
    };

    socket.onerror = (error) => {
      console.error('WebSocket连接错误:', error);
    };

    socket.onclose = () => {
      console.log('WebSocket连接已关闭');
      if (closedByUser || !reconnect) return;
      const delay = Math.min(reconnectMaxMs, reconnectMinMs * (2 ** Math.min(attempt, 4)));
      attempt += 1;
      clearReconnect();
      reconnectTimer = setTimeout(() => {
        if (closedByUser) return;
        try {
          ws = new WebSocketImpl(wsUrl);
          bind(ws);
        } catch (err) {
          console.error('WebSocket重连失败:', err);
        }
      }, delay);
    };
  };

  ws = new WebSocketImpl(wsUrl);
  bind(ws);

  return {
    get ws() { return ws; },
    close: () => {
      closedByUser = true;
      clearReconnect();
      try {
        if (ws && (ws.readyState === WebSocketImpl.OPEN || ws.readyState === 1 || ws.readyState === 0)) {
          ws.close();
        }
      } catch (_e) { /* ignore */ }
    }
  };
}
